/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import clsx from 'clsx';
import InfiniteLoader from 'react-virtualized/dist/commonjs/InfiniteLoader';
import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer';
import List from 'react-virtualized/dist/commonjs/List';
import { Empty, Spin } from 'antd';

import { Select } from '@atoms';
import { usePagesInfiniteList } from '@api/hooks';

import s from './styles.module.less';

const PAGE_SORTING_OPTION = {
  RECENT: {
    value: '-modified',
    label: 'Most recent page',
  },
  RELATED: {
    value: 'related',
    label: 'Child pages',
  },
  ALL: {
    value: '',
    label: 'All pages',
  },
};
const DEFAULT_PAGE_SORTING_OPTIONS = [PAGE_SORTING_OPTION.RECENT, PAGE_SORTING_OPTION.ALL];
const EVENT_PAGE_SORTING_OPTIONS = [PAGE_SORTING_OPTION.RELATED];

const PER_PAGE = 15;

const PageComboSelect = ({ forWeblive, competitionGroup, onAdd, isAdded, eventId, siteId, ...props }) => {
  
  // Define your static pages
  const staticPages = !competitionGroup ? [] : [
  {
    id: 'home',
    title: 'Home',
    linkUrl: `/`,
  },
  {
    id: 'live',
    title: 'LIVE',
    linkUrl: `/live`,
  },
  {
    id: 'news',
    title: 'News',
    linkUrl: `/news`
  },
  {
    id: 'timetable',
    title: 'Timetable',
    linkUrl: `/timetable`
  },
  {
    id: 'results',
    title: 'Results',
    linkUrl: `/results`
  },
  {
    id: 'athletes',
    title: 'Athletes',
    linkUrl: `/athletes`,
  },
  {
    id: 'countries',
    title: 'Countries',
    linkUrl: `/country/all`,
  },
  {
    id: 'medal-table',
    title: 'Medal Table',
    linkUrl: `/medaltable`,
  },
  {
    id: 'past-editions',
    title: 'Past Editions',
    linkUrl: `/event-calendar`,
  },
  {
    id: 'at-a-glance',
    title: 'At a Glance',
    linkUrl: `/highlights`,
  },
  {
    id: 'where-to-watch',
    title: 'Where to watch',
    slug: 'where-to-watch',
    linkUrl: `/where-to-watch`
  },
  {
    id: 'play-predictions',
    title: 'Play predictions',
    linkUrl: `/play-predictions`,
  },
  {
    id: 'gold',
    title: 'Gold',
    linkUrl: `/wct-gold`
  },
  {
    id: 'media',
    title: 'Media',
    linkUrl: `/multimedia`
  },
  {
    id: 'calendar-results',
    title: 'Calendar/Results',
    linkUrl: `/calendar-results`
  },
  {
    id: 'championship-timetable',
    title: 'Championship Timetable',
    linkUrl: `/timetable`,
  },
  {
    id: 'weekend-programme',
    title: 'Weekend Programme',
    linkUrl: `/programme`,
  },
  {
    id: 'my-athletics',
    title: 'My Athletics',
    linkUrl: '/my-athletics'
  },
  {
    id: 'event-stats',
    title: 'Event Stats',
    linkUrl: `/event-stats`,
  },
  {
    id: 'standings',
    title: 'Standings',
    linkUrl: `/standings/men`
  },
  {
    id: 'information',
    title: 'Information',
    linkUrl: `/information/organizer-details`
  },
  {
    id: 'global-running-conference',
    title: 'Global Running Conference',
    linkUrl: `/global-running-conference`
  }
];

  const sortingOptions = eventId ? EVENT_PAGE_SORTING_OPTIONS : DEFAULT_PAGE_SORTING_OPTIONS;
  const defaultSort = sortingOptions[0];

  const [sortOption, setSortOption] = React.useState(defaultSort.value);
  const { pages: fetchedPages, total, isFetching, fetchMore, canFetchMore } = usePagesInfiniteList({
    sort: sortOption,
    perPage: PER_PAGE,
    ...(eventId && { eventId }),
    ...(siteId && { siteId }),
  });

  // Combine static pages with fetched pages
  const allPages = React.useMemo(() => {
    return [...staticPages, ...fetchedPages];
  }, [fetchedPages]);

  const isRowLoaded = ({ index }) => {
    // Static pages are always loaded
    if (index < staticPages.length) return true;
    // For fetched pages, check if they're loaded
    const fetchedIndex = index - staticPages.length;
    return !canFetchMore || fetchedIndex < fetchedPages.length;
  };

  const renderRow = ({ index, style, key }) => {
    const currentPage = allPages[index];
    const added = isAdded(currentPage);
    const isStatic = index < staticPages.length;

    return (
      <div key={key} style={style} className={clsx(s.pageItem, { [s.added]: added, [s.static]: isStatic })}>
        {isRowLoaded({ index }) ? (
          <>
            <span className={s.pageItemTitle} title={currentPage?.title}>
              {currentPage?.title}
              {isStatic && <span className={s.staticLabel}></span>}
            </span>
            <button type="button" disabled={added} className={s.pageItemButton} onClick={() => onAdd(currentPage)}>
              +
            </button>
          </>
        ) : (
          <Spin style={{ margin: '0 auto' }} spinning />
        )}
      </div>
    );
  };

  const renderNoRows = () =>
    isFetching ? (
      <div style={{ textAlign: 'center' }}>
        <Spin spinning />
      </div>
    ) : (
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
    );

  const totalRows = staticPages.length + (fetchedPages.length === total ? total : fetchedPages.length + 1);
  
  const loadMoreRows = isFetching ? () => {} : () => {
    // Only fetch more if we're beyond the static pages
    fetchMore();
  };

  return (
    <div className={s.root} {...props}>
      {!forWeblive && <Select className={s.sortingField} value={sortOption} onChange={setSortOption} options={sortingOptions} />}

      <InfiniteLoader 
        isRowLoaded={isRowLoaded} 
        loadMoreRows={loadMoreRows} 
        rowCount={totalRows} 
        threshold={5}
      >
        {({ onRowsRendered, registerChild }) => (
          <AutoSizer disableHeight>
            {({ width }) => (
              <List
                ref={registerChild}
                className={s.pagesList}
                height={300}
                overscanRowCount={3}
                noRowsRenderer={renderNoRows}
                rowCount={totalRows}
                rowHeight={35}
                rowRenderer={renderRow}
                onRowsRendered={onRowsRendered}
                width={width}
              />
            )}
          </AutoSizer>
        )}
      </InfiniteLoader>
    </div>
  );
};

export default PageComboSelect;